import React, { useEffect, useState, useMemo } from "react";
import { api, getCurrencies } from "../lib/api";
import { useI18n } from "../i18n";
import { ArrowRightLeft } from "lucide-react";

export default function CurrencyConverter() {
  const { t, lang } = useI18n();
  const [currencies, setCurrencies] = useState([]);
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("BRL");
  const [amount, setAmount] = useState(1000);

  useEffect(() => {
    getCurrencies().then((d) => setCurrencies(d.currencies || []));
  }, []);

  const rates = useMemo(() => {
    const map = {};
    currencies.forEach((c) => {
      map[c.code] = c.rate_to_usd;
    });
    return map;
  }, [currencies]);

  const converted = useMemo(() => {
    const f = rates[from];
    const tg = rates[to];
    if (!f || !tg) return null;
    const inUsd = amount / f;
    return inUsd * tg;
  }, [rates, amount, from, to]);

  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  if (!currencies.length) return null;

  return (
    <div className="surface p-5" data-testid="currency-converter">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg">{t.market.converter_title}</h3>
        <span className="text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
          {t.common.live}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">
            {t.market.converter_from}
          </label>
          <select
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="mt-1 w-full bg-black/40 border border-white/10 rounded-md px-2 py-2 text-sm focus:outline-none focus:border-gold-500"
            data-testid="converter-from"
          >
            {currencies.map((c) => (
              <option key={c.code} value={c.code}>
                {c.flag} {c.code}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={swap}
          className="mb-0.5 h-10 w-10 rounded-md border border-white/10 bg-white/5 text-neutral-400 hover:text-gold-400 hover:border-gold-500/40 grid place-items-center transition-colors"
          data-testid="converter-swap"
          type="button"
        >
          <ArrowRightLeft size={14} />
        </button>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">
            {t.market.converter_to}
          </label>
          <select
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="mt-1 w-full bg-black/40 border border-white/10 rounded-md px-2 py-2 text-sm focus:outline-none focus:border-gold-500"
            data-testid="converter-to"
          >
            {currencies.map((c) => (
              <option key={c.code} value={c.code}>
                {c.flag} {c.code}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-4">
        <label className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">
          {t.market.converter_amount}
        </label>
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(Math.max(0, +e.target.value))}
          className="mt-1 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-right font-mono text-sm focus:outline-none focus:border-gold-500"
          data-testid="converter-amount"
        />
      </div>
      <div className="mt-5 border-t border-white/5 pt-4">
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-mono">
          {lang === "pt" ? "Resultado" : "Result"}
        </div>
        <div className="mt-1 font-mono text-3xl text-white" data-testid="converter-result">
          {converted !== null
            ? `${converted.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${to}`
            : "—"}
        </div>
      </div>
    </div>
  );
}
